import React, { useEffect, useRef, useState } from 'react'
import { askStream, getConversations, getConversation, deleteConversation, extractImage } from '../../services/askSoroby'
import ImageExtractCard from './imageExtractCard'
import Markdown from './markdown'
import './askSoroby.css'
import { useTranslation } from 'react-i18next';

// Roles allowed to use Ask Soroby. Mirrors the backend restriction
// (cleaners/drivers can't see business financials), so we don't show a widget
// that would only ever return 401/Unauthorized for them.
const ALLOWED_ROLES = ['admin', 'staff', 'order_desk']

// Remembers the active conversation so it survives a page refresh.
const STORAGE_KEY = 'askSoroby.conversationId'

// Translation keys only — the text is resolved with t() at render time (and the
// translated question is what gets sent, since the user asked in that language).
const SUGGESTIONS = [
    { icon: 'fa-clock', textKey: 'askSoroby.suggestPending' },
    { icon: 'fa-calendar-day', textKey: 'askSoroby.suggestScheduledToday' },
    { icon: 'fa-sack-dollar', textKey: 'askSoroby.suggestRevenueThisMonth' },
    { icon: 'fa-file-invoice-dollar', textKey: 'askSoroby.suggestUnpaidInvoices' },
    { icon: 'fa-user-clock', textKey: 'askSoroby.suggestInactiveClients' },
    { icon: 'fa-ranking-star', textKey: 'askSoroby.suggestTopServices' },
]

// Sidebar date buckets: internal id -> translation key for the visible label.
const GROUP_LABEL_KEYS = {
    Today: 'common.today',
    Yesterday: 'common.yesterday',
    'Previous 7 days': 'askSoroby.previous7Days',
    Older: 'askSoroby.older',
}

// Group conversations into dated buckets for the sidebar, filtered by search.
const groupConversations = (list, search) => {
    const q = search.trim().toLowerCase()
    const filtered = q ? list.filter((c) => (c.title || '').toLowerCase().includes(q)) : list
    const now = new Date()
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const startYesterday = startToday - 86400000
    const start7 = startToday - 6 * 86400000
    const buckets = { Today: [], Yesterday: [], 'Previous 7 days': [], Older: [] }
    filtered.forEach((c) => {
        const t = new Date(c.updatedAt).getTime()
        if (t >= startToday) buckets.Today.push(c)
        else if (t >= startYesterday) buckets.Yesterday.push(c)
        else if (t >= start7) buckets['Previous 7 days'].push(c)
        else buckets.Older.push(c)
    })
    return ['Today', 'Yesterday', 'Previous 7 days', 'Older']
        .map((label) => ({ label, items: buckets[label] }))
        .filter((g) => g.items.length)
}

const AskSoroby = ({ currentUser }) => {
    const [open, setOpen] = useState(false)
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const [input, setInput] = useState('')
    const [messages, setMessages] = useState([])
    const [conversationId, setConversationId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [conversations, setConversations] = useState([])
    const [loadingConversation, setLoadingConversation] = useState(false)
    const [search, setSearch] = useState('')
    const [atBottom, setAtBottom] = useState(true)
    const [copiedIdx, setCopiedIdx] = useState(null)
    const [imageDraft, setImageDraft] = useState(null)
    const [extractingImage, setExtractingImage] = useState(false)
    const [dragOver, setDragOver] = useState(false)
    const { t } = useTranslation();

    const bottomRef = useRef(null)
    const bodyRef = useRef(null)
    const inputRef = useRef(null)
    const fileRef = useRef(null)
    const initRef = useRef(false)
    // Bumped on every scan AND on chat switches, so a scan that resolves after
    // the user navigated away can't patch the wrong conversation's messages.
    const scanSeqRef = useRef(0)

    const roles = currentUser?.activeRoles ?? []
    const canUse = roles.some((r) => ALLOWED_ROLES.includes(r))

    // Auto-scroll to newest message.
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, loading])

    // Persist the active conversation id across refreshes. Only writes — we
    // never clear it here, otherwise the initial null on each page load would
    // wipe the saved chat before it's restored. Clearing is explicit (newChat).
    useEffect(() => {
        if (conversationId) localStorage.setItem(STORAGE_KEY, conversationId)
    }, [conversationId])

    // Focus the input when opening; Esc closes the panel. Also lazily load the
    // history + last conversation the FIRST time the panel is opened — never on
    // plain page loads, so we don't add a DB query to every dashboard page.
    useEffect(() => {
        if (!open) return
        if (!initRef.current) {
            initRef.current = true
            refreshConversations()
            const saved = localStorage.getItem(STORAGE_KEY)
            if (saved) loadConversation(saved)
        }
        const t = setTimeout(() => inputRef.current?.focus(), 80)
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
        window.addEventListener('keydown', onKey)
        return () => { clearTimeout(t); window.removeEventListener('keydown', onKey) }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // Allow other parts of the app (e.g. the dashboard "Ask Soroby" quick action)
    // to open the assistant via a custom window event.
    useEffect(() => {
        const onOpen = () => setOpen(true)
        window.addEventListener('soroby:open-ask', onOpen)
        return () => window.removeEventListener('soroby:open-ask', onOpen)
    }, [])

    // Lets other parts of the app (the Overview dashboard's "Scan" button) hand
    // off an already-picked image file: open the panel and feed it straight
    // into the same scan flow as attaching one inside the chat. Re-subscribes
    // when extractingImage/loading change so handleImageFile's own busy-guard
    // below always sees current values, not a stale closure from mount.
    useEffect(() => {
        const onScanImage = (e) => {
            const file = e.detail?.file
            if (file) {
                // Start a fresh chat first — if this is the panel's first open
                // this session, the [open] effect below would otherwise restore
                // the last saved conversation a moment later and wipe out the
                // scan message (and invalidate the in-flight extraction via
                // scanSeqRef). newChat() also clears the saved-conversation id
                // from localStorage synchronously, so that effect finds nothing
                // to restore.
                newChat()
            }
            setOpen(true)
            if (file) handleImageFile(file)
        }
        window.addEventListener('soroby:scan-image', onScanImage)
        return () => window.removeEventListener('soroby:scan-image', onScanImage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extractingImage, loading])

    if (!canUse) return null

    const refreshConversations = async () => {
        try {
            const res = await getConversations()
            setConversations(res?.code === 200 && Array.isArray(res.message) ? res.message : [])
        } catch (e) { /* keep existing on transient error */ }
    }

    const loadConversation = async (id) => {
        if (id === conversationId && !loadingConversation) return
        // Switch instantly: highlight the chat + clear the pane, then load.
        scanSeqRef.current++
        setConversationId(id)
        setMessages([])
        setImageDraft(null)
        setLoadingConversation(true)
        try {
            const res = await getConversation(id)
            if (res?.code === 200 && res.message) {
                setMessages((res.message.messages || []).map((m) => ({ role: m.role, content: m.content })))
            } else {
                // Conversation gone (e.g. deleted) — reset to a new chat.
                setConversationId(null)
                setMessages([])
                localStorage.removeItem(STORAGE_KEY)
                
            }
        } catch (e) { /* keep current (empty) state */ } finally {
            setLoadingConversation(false)
            setTimeout(() => inputRef.current?.focus(), 60)
        }
    }

    const newChat = () => {
        scanSeqRef.current++
        setMessages([])
        setConversationId(null)
        setImageDraft(null)
        setInput('')
        localStorage.removeItem(STORAGE_KEY)
        if (inputRef.current) inputRef.current.style.height = 'auto'
        setTimeout(() => inputRef.current?.focus(), 60)
    }

    const removeConversation = async (id, e) => {
        e.stopPropagation()
        // Optimistic removal.
        setConversations((prev) => prev.filter((c) => c.id !== id))
        if (id === conversationId) newChat()
        try { await deleteConversation(id) } catch (err) { refreshConversations() }
    }

    const copyMessage = (text, idx) => {
        navigator.clipboard?.writeText(text).then(() => {
            setCopiedIdx(idx)
            setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1500)
        }).catch(() => {})
    }

    const autoGrow = (el) => {
        el.style.height = 'auto'
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`
    }

    const onBodyScroll = () => {
        const el = bodyRef.current
        if (!el) return
        setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80)
    }

    const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })

    // Strip the scan animation from any image bubbles once extraction settles.
    const clearScanning = (list) => list.map((m) => (m.scanning ? { ...m, scanning: false } : m))

    // Upload one image for extraction. The photo shows up in the chat right
    // away (with a scan animation) so the 5–10s the vision model needs never
    // feels like a dead click.
    const handleImageFile = async (file) => {
        if (!file || extractingImage || loading) return
        if (!file.type?.startsWith('image/')) return
        const preview = URL.createObjectURL(file)
        const ticket = ++scanSeqRef.current
        setImageDraft(null)
        setExtractingImage(true)
        setMessages((prev) => [
            ...prev,
            { role: 'user', content: '', image: preview, scanning: true },
            { role: 'assistant', content: '', streaming: true, status: t('askSoroby.readingImage') },
        ])
        try {
            const res = await extractImage(file)
            if (ticket !== scanSeqRef.current) return // user switched chats mid-scan
            if (res?.code !== 200) throw new Error(typeof res?.message === 'string' ? res.message : t('askSoroby.imageScanFailed'))
            // The server keeps nothing — the local preview is the card thumbnail.
            const draft = { ...res.message, imageUrl: preview }
            setImageDraft(draft)
            setMessages((prev) => clearScanning(patchLastAssistant(prev, {
                content: draft?.summary || t('askSoroby.imageReadSummary'),
                streaming: false,
                status: '',
            })))
        } catch (err) {
            if (ticket !== scanSeqRef.current) return
            setMessages((prev) => clearScanning(patchLastAssistant(prev, {
                content: err?.message || t('askSoroby.imageReadFailed'),
                error: true,
                streaming: false,
                status: '',
            })))
        } finally {
            setExtractingImage(false)
            if (fileRef.current) fileRef.current.value = ''
        }
    }

    const onPickImage = (e) => handleImageFile(e.target.files?.[0])

    // Paste a screenshot straight into the message box.
    const onPaste = (e) => {
        const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith('image/'))
        if (!item) return
        e.preventDefault()
        handleImageFile(item.getAsFile())
    }

    // Drag & drop an image anywhere onto the chat.
    const onDragOver = (e) => {
        if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return
        e.preventDefault()
        setDragOver(true)
    }
    const onDragLeave = (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return
        setDragOver(false)
    }
    const onDrop = (e) => {
        e.preventDefault()
        setDragOver(false)
        handleImageFile(e.dataTransfer?.files?.[0])
    }

    const finishDraft = (text) => {
        setImageDraft(null)
        setMessages((prev) => [...prev, { role: 'assistant', content: text }])
    }

    // Patch the last message (the streaming assistant bubble) immutably.
    const patchLastAssistant = (list, patch) => {
        const copy = list.slice()
        const last = copy[copy.length - 1]
        if (last && last.role === 'assistant') copy[copy.length - 1] = { ...last, ...patch }
        return copy
    }

    const send = async (text) => {
        const question = (text ?? input).trim()
        if (!question || loading || extractingImage) return

        const wasNew = !conversationId
        setInput('')
        if (inputRef.current) inputRef.current.style.height = 'auto'
        // Add the user message + an empty streaming assistant bubble (shows the
        // typing dots until the first token arrives).
        setMessages((prev) => [
            ...prev,
            { role: 'user', content: question },
            { role: 'assistant', content: '', streaming: true },
        ])
        setLoading(true)

        let acc = ''
        await askStream(question, conversationId, {
            onMeta: (e) => { if (e.conversationId) setConversationId(e.conversationId) },
            onStatus: (label) => {
                setMessages((prev) => patchLastAssistant(prev, { status: label }))
            },
            onToken: (tok) => {
                acc += tok
                // First real token: the answer is arriving, drop the status line.
                setMessages((prev) => patchLastAssistant(prev, { content: acc, status: '' }))
            },
            onError: (msg) => {
                setMessages((prev) => patchLastAssistant(prev, {
                    content: msg || t('askSoroby.error'),
                    error: true,
                    streaming: false,
                }))
            },
            onDone: () => {},
        })

        setMessages((prev) => patchLastAssistant(prev, { streaming: false }))
        setLoading(false)
        if (wasNew) refreshConversations()
    }

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
        }
    }

    const groups = groupConversations(conversations, search)

    return (
        <>
            {/* Floating launcher */}
            <button
                className={`ask-soroby-fab ${open ? 'hidden' : ''}`}
                onClick={() => setOpen(true)}
                title={t('askSoroby.title')}
                aria-label={t('askSoroby.title')}
            >
                <i className="fa-solid fa-wand-magic-sparkles"></i>
                <span className="ask-soroby-fab-label">{t('askSoroby.title')}</span>
            </button>

            {open && (
                <div className={`ask-soroby-panel ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>

                    {/* Sidebar */}
                    <aside className="ask-soroby-sidebar">
                        <div className="ask-soroby-sidebar-head">
                            <span>{t('askSoroby.conversations')}</span>
                            <button
                                className="ask-soroby-collapse"
                                onClick={() => setSidebarOpen(false)}
                                title={t('askSoroby.hideSidebar')}
                                aria-label={t('askSoroby.hideSidebar')}
                            >
                                <i className="fa-solid fa-angles-left"></i>
                            </button>
                        </div>

                        <button className="ask-soroby-newchat" onClick={newChat}>
                            <i className="fa-solid fa-plus"></i> {t('askSoroby.newChat')}
                        </button>

                        <div className="ask-soroby-search">
                            <i className="fa-solid fa-magnifying-glass"></i>
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('askSoroby.searchChats')}
                            />
                        </div>

                        <div className="ask-soroby-sidebar-list">
                            {groups.length === 0 && (
                                <div className="ask-soroby-hist-empty">
                                    {search ? t('askSoroby.noMatches') : t('askSoroby.noChatsYet')}
                                </div>
                            )}
                            {groups.map((g) => (
                                <div key={g.label} className="ask-soroby-hist-group">
                                    <div className="ask-soroby-hist-grouplabel">{t(GROUP_LABEL_KEYS[g.label])}</div>
                                    {g.items.map((c) => (
                                        <div
                                            key={c.id}
                                            className={`ask-soroby-hist-item ${c.id === conversationId ? 'active' : ''}`}
                                            onClick={() => loadConversation(c.id)}
                                            title={c.title || t('askSoroby.untitledChat')}
                                        >
                                            <i className="fa-regular fa-message ask-soroby-hist-icon"></i>
                                            <span className="ask-soroby-hist-title">{c.title || t('askSoroby.untitledChat')}</span>
                                            <button
                                                className="ask-soroby-hist-del"
                                                onClick={(e) => removeConversation(c.id, e)}
                                                title={t('askSoroby.deleteChat')}
                                                aria-label={t('askSoroby.deleteChat')}
                                            >
                                                <i className="fa-solid fa-trash-can"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </aside>

                    {/* Main */}
                    <div className="ask-soroby-main" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
                        {dragOver && (
                            <div className="ask-soroby-drop">
                                <i className="fa-regular fa-image"></i>
                                <span>{t('askSoroby.dropImage')}</span>
                            </div>
                        )}
                        <div className="ask-soroby-header">
                            {!sidebarOpen && (
                                <button
                                    className="ask-soroby-iconbtn"
                                    onClick={() => setSidebarOpen(true)}
                                    title={t('askSoroby.showChats')}
                                    aria-label={t('askSoroby.showChats')}
                                >
                                    <i className="fa-solid fa-angles-right"></i>
                                </button>
                            )}
                            <div className="ask-soroby-brand">
                                <div className="ask-soroby-brand-avatar">
                                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                                </div>
                                <div className="ask-soroby-brand-text">
                                    <div className="ask-soroby-brand-name">{t('askSoroby.title')}</div>
                                    <div className="ask-soroby-subtitle">
                                        <span className="ask-soroby-dot"></span> {t('askSoroby.subtitle')}
                                    </div>
                                </div>
                            </div>
                            <button className="ask-soroby-iconbtn" onClick={() => setOpen(false)} aria-label={t('common.close')}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <div className="ask-soroby-body" ref={bodyRef} onScroll={onBodyScroll}>
                            {loadingConversation && (
                                <div className="ask-soroby-skeleton">
                                    <div className="as-sk assistant lg"></div>
                                    <div className="as-sk user"></div>
                                    <div className="as-sk assistant"></div>
                                    <div className="as-sk user sm"></div>
                                    <div className="as-sk assistant md"></div>
                                </div>
                            )}

                            {!loadingConversation && messages.length === 0 && (
                                <div className="ask-soroby-welcome">
                                    <div className="ask-soroby-welcome-avatar">
                                        <i className="fa-solid fa-wand-magic-sparkles"></i>
                                    </div>
                                    <h4>{t('askSoroby.welcome')}</h4>
                                    <p>{t('askSoroby.welcomeHint')}</p>
                                    <div className="ask-soroby-suggestions">
                                        {SUGGESTIONS.map((s) => (
                                            <button key={s.textKey} className="ask-soroby-chip" onClick={() => send(t(s.textKey))}>
                                                <i className={`fa-solid ${s.icon}`}></i>
                                                <span>{t(s.textKey)}</span>
                                            </button>
                                        ))}
                                        <button className="ask-soroby-chip scan" onClick={() => fileRef.current?.click()}>
                                            <i className="fa-solid fa-camera"></i>
                                            <span>{t('askSoroby.scanHint')}</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!loadingConversation && messages.map((m, i) => (
                                <div key={i} className={`ask-soroby-row ${m.role}`}>
                                    {m.role === 'assistant' && (
                                        <div className="ask-soroby-avatar">
                                            <i className="fa-solid fa-wand-magic-sparkles"></i>
                                        </div>
                                    )}
                                    <div className={`ask-soroby-msg ${m.role} ${m.error ? 'error' : ''} ${m.streaming ? 'streaming' : ''} ${m.image ? 'has-image' : ''}`}>
                                        {m.image
                                            ? <span className={`ask-soroby-scanwrap ${m.scanning ? 'scanning' : ''}`}>
                                                <img className="ask-soroby-msg-img" src={m.image} alt={t('askSoroby.uploadedScan')} />
                                              </span>
                                            : m.role === 'assistant' && !m.error
                                                ? (m.streaming && !m.content
                                                    ? <span className="ask-soroby-status-line">
                                                        {m.status && <span className="ask-soroby-status-label">{m.status}</span>}
                                                        <span className="ask-soroby-typing"><span></span><span></span><span></span></span>
                                                      </span>
                                                    : <Markdown text={m.content} />)
                                                : m.content}
                                        {m.role === 'assistant' && !m.error && !m.streaming && m.content && (
                                            <button
                                                className="ask-soroby-copy"
                                                onClick={() => copyMessage(m.content, i)}
                                                title={t('common.copy')}
                                                aria-label={t('common.copy')}
                                            >
                                                <i className={copiedIdx === i ? 'fa-solid fa-check' : 'fa-regular fa-copy'}></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {!loadingConversation && imageDraft && (
                                <ImageExtractCard
                                    key={imageDraft.imageUrl || 'draft'}
                                    draft={imageDraft}
                                    onDone={finishDraft}
                                    onDiscard={() => setImageDraft(null)}
                                />
                            )}
                            <div ref={bottomRef} />
                        </div>

                        {!atBottom && messages.length > 0 && (
                            <button className="ask-soroby-scrolldown" onClick={scrollToBottom} aria-label={t('askSoroby.scrollToLatest')}>
                                <i className="fa-solid fa-arrow-down"></i>
                            </button>
                        )}

                        <div className="ask-soroby-input">
                            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" onChange={onPickImage} hidden />
                            <button
                                type="button"
                                className="ask-soroby-attach"
                                onClick={() => fileRef.current?.click()}
                                disabled={loading || extractingImage}
                                aria-label={t('askSoroby.scanImage')}
                                title={t('askSoroby.scanHint')}
                            >
                                <i className={extractingImage ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-image'}></i>
                            </button>
                            <textarea
                                ref={inputRef}
                                rows={1}
                                value={input}
                                placeholder={t('askSoroby.inputPlaceholder')}
                                onChange={(e) => { setInput(e.target.value); autoGrow(e.target) }}
                                onKeyDown={onKeyDown}
                                onPaste={onPaste}
                                disabled={loading}
                            />
                            <button onClick={() => send()} disabled={loading || !input.trim()} aria-label={t('invoiceShare.send')}>
                                <i className="fa-solid fa-arrow-up"></i>
                            </button>
                        </div>

                        <div className="ask-soroby-footer">
                            {t('askSoroby.footer')}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default AskSoroby
